import { DateLike } from './time'

/**
 * Find the closest date which occures on or before the given date.
 */
export function getClosestDate(
  appointments: DateLike[] = [],
  compareDate: DateLike = new Date()
) {
  return appointments
    ?.map((x) => new Date(x))
    .sort((x, y) => x.getTime() - y.getTime())
    .find((d) => d >= new Date(compareDate))
}

export function getStartOfDay(date: DateLike) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function getEndOfDay(date: DateLike) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

/**
 * Add/subtrack seconds from the given date.
 */
export function addMilliToDate(
  milli: number = 1000,
  date: DateLike = new Date()
) {
  if (typeof date !== 'object') date = new Date(date)
  return new Date(date.getTime() + milli)
}

/**
 * Add some number of months to a date object.
 */
export function addMonthsToDate(date: DateLike = new Date(), months = 1) {
  if (typeof date !== 'object') date = new Date(date)

  return new Date(date.setMonth(date.getMonth() + months))
}

/**
 * Add some number of days to a date object.
 */
export function addDaysToDate(date: DateLike = new Date(), days = 1) {
  if (typeof date !== 'object') date = new Date(date)

  return new Date(new Date(date).setDate(date.getDate() + days))
}

/**
 * Get the number of days in the given month index.
 */
export function daysInMonth(month = 0, year = new Date().getFullYear()) {
  return new Date(year, month + 1, 0).getDate()
}

/**
 * Return the appointment that is currently active (now falls within its window),
 * or the next upcoming one, or the most recent past appointment if all are in the past.
 * Returns undefined when the list is empty.
 *
 * NOTE — "fallback" (most recent past): when a guest card has multiple appointments
 * from different dates (e.g. a reschedule), older appointments may carry stale
 * unit stops and lock codes. Falling back to the most recently scheduled
 * past appointment ensures we use the latest provisioned access data.
 */
export function getActiveAppointment<
  T extends { scheduled: string; scheduledEnd?: string }
>(appointments: T[]): T | undefined {
  if (!appointments?.length) return undefined
  const now = new Date()
  const inWindow = appointments.find((a) => {
    const start = new Date(a.scheduled)
    const end = a.scheduledEnd ? new Date(a.scheduledEnd) : null
    return now >= start && (end == null || now < end)
  })
  if (inWindow) return inWindow
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.scheduled).getTime() - new Date(b.scheduled).getTime()
  )
  return (
    sorted.find((a) => new Date(a.scheduled) > now) ?? sorted[sorted.length - 1]
  )
}

/**
 * Get the number of milliseconds until the given date. Returns a negative
 * number if the date has passed.
 */
export function getMillisecondsUntil(
  date: DateLike,
  /**
   * The date to compare against. Defaults to now.
   */
  now: DateLike = new Date()
) {
  return new Date(date).getTime() - new Date(now).getTime()
}
